/*
  h26-atc.js: Harvest 2026 add to cart. Loaded only through snippets/h26-head.liquid.

  Handles <form data-h26-atc> only. Every other form keeps its current behaviour.

  Form contract:
    <form method="post" action="/cart/add" data-h26-atc data-placement="h26-lp-sets"
          data-msg-soldout="..." data-msg-limit="... {n} ..." data-msg-limit-unknown="..."
          data-msg-network="..." data-msg-error="...">
      <input type="hidden" name="id" value="VARIANT_ID">
      <input type="hidden" name="quantity" value="1">         (optional, default 1)
      <input type="hidden" name="properties[_closest]" value="1">  (optional pass-through)
      <button type="submit">...</button>
      <p data-h26-msg role="status" aria-live="polite"></p>     (optional, created if absent)
    </form>
  - `_source` = the form's data-placement (or the closest [data-placement]); always set.
  - Messages come from data-msg-* on the form or the closest [data-h26-msgs] holder; the
    English strings below are fallbacks only. {n} is replaced by the quantity left.
  - Try instead hook: on sold out, [data-h26-try] inside the closest [data-h26-card] (or
    the form) is un-hidden, and an `h26:soldout` event bubbles from the form.
  - States: data-h26-state on the form = loading | success | soldout | limit | network | error.
  - Events: `h26:atc` bubbles from the form with the result; oo_atc_result is published.

  UpCart (app bundle upcart-2-424, read Oct 12 2026):
  - Its form interceptor asks window.upcartShouldAddToCartContinue(submitEvent); we answer
    false for h26 forms only and pass every other call to the previous function.
  - Its network interceptor asks window.upcartShouldSkipAddToCart(requestUrl) for every
    /cart/add call; we answer true only for our own requests (marked ?oo_h26=1), so UpCart
    neither re-opens nor double refreshes on them. Every other call goes to the previous
    function.
  - On success we call upcartRefreshCart() and then upcartOpenCart() ourselves.
  - The submit is also caught in the capture phase, so no other theme listener sees it.

  Same bottle twice (DESIGN §2, LP spec §5.3), added Oct 12 with the single product page:
  before adding, the cart is read; if the variant is already a line (no selling plan), the new
  add reuses that line's properties (its `_source`), so the line doesn't split. If the cart
  read fails, the add goes ahead with this form's own `_source`.
  Messages: data-msg-* on the form or an ancestor, else window.OO_H26.msgs (set by the page's
  main section from its settings), else the English fallbacks below.
*/
(function () {
  'use strict';
  if (window.OOH26 && window.OOH26.__atc) return;

  var MARK = 'oo_h26=1';
  var FORM_SEL = 'form[data-h26-atc]';
  var DEBOUNCE_MS = 600;
  var FALLBACK = {
    soldout: 'Allocation filled',
    limit: 'Only {n} left in your market',
    limit_unknown: 'No more available in your market',
    network: 'Could not reach the cart. Please try again.',
    error: 'Could not add to cart. Please try again.'
  };

  function isH26Form(el) {
    try { return !!(el && el.closest && el.closest(FORM_SEL)); } catch (e) { return false; }
  }

  /* ---------- UpCart hooks: preserve and chain previous functions ---------- */
  function installHooks() {
    var prevSkip = window.upcartShouldSkipAddToCart;
    if (!prevSkip || !prevSkip.__h26) {
      var skip = function (arg) {
        try {
          var u = typeof arg === 'string' ? arg : (arg && (arg.url || arg.href)) || '';
          if (String(u).indexOf(MARK) !== -1) return true;
          if (arg && arg.nodeType === 1 && isH26Form(arg)) return true;
          if (arg && arg.target && isH26Form(arg.target)) return true;
        } catch (e) {}
        return typeof prevSkip === 'function' ? prevSkip.apply(this, arguments) : false;
      };
      skip.__h26 = true;
      window.upcartShouldSkipAddToCart = skip;
    }
    var prevCont = window.upcartShouldAddToCartContinue;
    if (!prevCont || !prevCont.__h26) {
      var cont = function (ev) {
        try { if (ev && isH26Form(ev.target)) return false; } catch (e) {}
        return typeof prevCont === 'function' ? prevCont.apply(this, arguments) : true;
      };
      cont.__h26 = true;
      window.upcartShouldAddToCartContinue = cont;
    }
  }

  /* ---------- helpers ---------- */
  function msgFor(form, key, n) {
    var a = 'data-msg-' + key.replace(/_/g, '-');
    var holder = form.hasAttribute(a) ? form : form.closest('[' + a + ']');
    var cfg = (window.OO_H26 && window.OO_H26.msgs) || {};
    var s = holder ? holder.getAttribute(a) : (cfg[key] != null && cfg[key] !== '' ? cfg[key] : FALLBACK[key]);
    if (s == null) s = FALLBACK[key] || '';
    return n != null ? s.replace(/\{n\}/g, String(n)) : s;
  }

  function msgEl(form) {
    var el = form.querySelector('[data-h26-msg]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-h26-msg', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.className = 'h26-atc-msg';
      form.appendChild(el);
    }
    return el;
  }

  function setState(form, state, text) {
    form.setAttribute('data-h26-state', state);
    var card = form.closest('[data-h26-card]');
    if (card) card.setAttribute('data-h26-state', state);
    var m = msgEl(form);
    m.textContent = text || '';
    m.hidden = !text;
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      var busy = state === 'loading';
      btn.disabled = busy;
      if (busy) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
    }
  }

  function placementOf(form) {
    var p = form.getAttribute('data-placement');
    if (!p) {
      var n = form.closest('[data-placement]');
      p = n ? n.getAttribute('data-placement') : '';
    }
    return p || 'h26-unplaced';
  }

  function buildBody(form) {
    var fd = new FormData(form);
    var props = {};
    fd.forEach(function (v, k) {
      var m = /^properties\[(.+)\]$/.exec(k);
      if (m && typeof v === 'string') props[m[1]] = v;
    });
    props._source = placementOf(form);
    var qty = parseInt(fd.get('quantity'), 10);
    var item = { id: parseInt(fd.get('id'), 10), quantity: qty > 0 ? qty : 1, properties: props };
    var sp = fd.get('selling_plan');
    if (sp) item.selling_plan = parseInt(sp, 10);
    return item;
  }

  // Shopify 422 texts (English storefront): "... is already sold out.",
  // "Only 50 items were added to your cart due to availability.",
  // "You can only add 3 of this item to your cart." Codes are ours; Shopify's text never
  // leaves this function.
  function classify422(body) {
    var t = ((body && (body.description || body.message)) || '') + '';
    if (/sold out/i.test(t)) return { outcome: 'soldout', code: 'sold_out' };
    var n = /(\d+)/.exec(t);
    if (/only|availability|can only add/i.test(t)) {
      return { outcome: 'limit', code: 'quantity_limit', n: n ? parseInt(n[1], 10) : null };
    }
    if (/cannot find variant/i.test(t)) return { outcome: 'error', code: 'variant_not_found' };
    return { outcome: 'error', code: 'cart_422' };
  }

  function refreshCart(open) {
    var p;
    try { p = typeof window.upcartRefreshCart === 'function' ? window.upcartRefreshCart() : null; } catch (e) { p = null; }
    return Promise.resolve(p).catch(function () {}).then(function () {
      if (open) {
        try {
          if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
          else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (e) {}
      }
    });
  }

  function report(form, item, res) {
    var detail = {
      outcome: res.outcome,
      error_code: res.code || null,
      status: res.status || null,
      variant_id: item.id || null,
      quantity: item.quantity,
      added_qty: res.added != null ? res.added : null,
      source: item.properties ? item.properties._source : null,
      // Test 4: the visible position, when the row carries it (snippets/oo-ab-sets); absent elsewhere, so the payload is unchanged
      index: (form.closest && form.closest('[data-h26-index]')) ? form.closest('[data-h26-index]').getAttribute('data-h26-index') : null
    };
    try { form.dispatchEvent(new CustomEvent('h26:atc', { bubbles: true, detail: detail })); } catch (e) {}
    try { if (window.OOH26 && window.OOH26.track) window.OOH26.track('atc_result', detail, form); } catch (e) {}
  }

  // Same bottle twice: reuse the existing line's properties so the line doesn't split.
  function reuseLine(item) {
    if (item.selling_plan) return Promise.resolve();
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        var lines = (c && c.items) || [];
        for (var i = 0; i < lines.length; i++) {
          var l = lines[i];
          if (l.variant_id === item.id && !l.selling_plan_allocation) {
            var p = {};
            var src = l.properties || {};
            for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k) && src[k] != null) p[k] = src[k];
            item.properties = p;
            return;
          }
        }
      })
      .catch(function () {});
  }

  /* ---------- submit ---------- */
  function submit(form) {
    var now = Date.now();
    if (form.__h26busy || (form.__h26last && now - form.__h26last < DEBOUNCE_MS)) return;
    form.__h26busy = true;
    form.__h26last = now;

    var item = buildBody(form);
    if (!item.id) {
      setState(form, 'error', msgFor(form, 'error'));
      report(form, item, { outcome: 'error', code: 'no_variant' });
      form.__h26busy = false;
      return;
    }
    setState(form, 'loading', '');

    reuseLine(item).then(function () { return fetch('/cart/add.js?' + MARK, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [item] })
    }); }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (body) { return { r: r, body: body }; });
    }).then(function (x) {
      if (x.r.ok) {
        setState(form, 'success', '');
        report(form, item, { outcome: 'success', status: x.r.status, added: item.quantity });
        // data-h26-no-open (Build your own, Oct 20): refresh the drawer without opening it; the page shows its own helper
        return refreshCart(!form.hasAttribute('data-h26-no-open'));
      }
      if (x.r.status === 422) {
        var c = classify422(x.body);
        if (c.outcome === 'soldout') {
          setState(form, 'soldout', msgFor(form, 'soldout'));
          var scope = form.closest('[data-h26-card]') || form;
          [].forEach.call(scope.querySelectorAll('[data-h26-try]'), function (el) { el.hidden = false; });
          try { form.dispatchEvent(new CustomEvent('h26:soldout', { bubbles: true, detail: { variant_id: item.id } })); } catch (e) {}
          report(form, item, { outcome: 'soldout', code: c.code, status: 422, added: 0 });
          return refreshCart(false);
        }
        if (c.outcome === 'limit') {
          var text = c.n != null ? msgFor(form, 'limit', c.n) : msgFor(form, 'limit_unknown');
          setState(form, 'limit', text);
          // Shopify may add the available quantity on this answer, so the drawer is
          // refreshed (not opened) to stay correct.
          report(form, item, { outcome: 'limit', code: c.code, status: 422, added: /added/i.test((x.body && x.body.description) || '') ? c.n : 0 });
          return refreshCart(false);
        }
        setState(form, 'error', msgFor(form, 'error'));
        report(form, item, { outcome: 'error', code: c.code, status: 422 });
        return refreshCart(false);
      }
      setState(form, 'error', msgFor(form, 'error'));
      report(form, item, { outcome: 'error', code: 'http_' + x.r.status, status: x.r.status });
    }).catch(function () {
      setState(form, 'network', msgFor(form, 'network'));
      report(form, item, { outcome: 'network', code: 'network' });
    }).then(function () {
      form.__h26busy = false;
    });
  }

  function onSubmit(e) {
    var form = e.target && e.target.closest ? e.target.closest(FORM_SEL) : null;
    if (!form) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    submit(form);
  }

  // Oct 26 (REQ-003): wallet buttons submit the page's form straight to checkout, so its hidden _source
  // ([data-h26-wallet-src], "<placement>-wallet") also names where the visitor came from: ":<oo_src>" from the link.
  function walletSrc() {
    try {
      var from = new URLSearchParams(location.search).get('oo_src');
      if (!from || !/^[A-Za-z0-9_\-]{1,48}$/.test(from)) return;
      Array.prototype.forEach.call(document.querySelectorAll('input[data-h26-wallet-src]'), function (i) {
        if (i.value.indexOf(':') === -1) i.value = i.value + ':' + from;
      });
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', walletSrc); else walletSrc();

  installHooks();
  // UpCart may define its defaults after us; re-check once it has loaded.
  window.addEventListener('load', installHooks);
  document.addEventListener('submit', onSubmit, true);

  window.OOH26 = window.OOH26 || {};
  window.OOH26.__atc = true;
  window.OOH26.addToCart = submit;
})();
